import styled, { css } from "styled-components";

export interface SectionProps {
  flexDir?: string;
  gap?: string;
  position?: string;
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
  disabled?: boolean;
  align?: string;
  margin?: string;
  border?: string;
  borderTop?: string;
  borderRight?: string;
  borderLeft?: string;
  borderBottom?: string;
  borderRadius?: string;
  width?: string;
}

interface TextAreaProps {
  borderColor: string;
}

interface DividerProps {
  bgColor?: string;
  margin?: string;
}

interface UpdateHistoryProps {
  disabled?: boolean;
}

interface ButtonProps {
  bgColor?: string;
  borderRadius?: string;
  padding?: string;
  color?: string;
  transition?: string;
  border?: string;
  disabled?: boolean;
  width?: string;
  height?: string;
  display?: string;
  align?: string;
  justify?: string;
  zIndex?: string;
  margin?: string;
  position?: string;
  top?: string;
  right?: string;
  left?: string;
  bottom?: string;
}

interface ContainerProps {
  modalIsOpen?: boolean;
}

interface OverlayProps {
  isOpen?: boolean;
}

export const Container = styled.div<ContainerProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 1rem 0;

  ${({modalIsOpen}) => modalIsOpen && css`
    position: fixed;
  `}
`

export const Header = styled.header`
  color: black;
  font-weight: 600;
  font-size: 16px;
`

export const Section = styled.section<SectionProps>`
  width: ${props => props.width ? props.width : "100%"};
  display: flex;
  flex-direction: ${props => props.flexDir ? props.flexDir : "row"};
  align-items: ${props => props.align ? props.align : ""};
  gap: ${props => props.gap ? props.gap : "1.6rem"};
  position: ${props => props.position ? props.position : ""};
  margin: ${props => props.margin ? props.margin : ""};

  border: ${props => props.border ? props.border : ""};
  border-radius: ${props => props.borderRadius ? props.borderRadius : ""};
  border-right: ${props => props.borderRight ? props.borderRight : ""};
  border-left: ${props => props.borderLeft ? props.borderLeft : ""};
  border-top: ${props => props.borderTop ? props.borderTop : ""};
  border-bottom: ${props => props.borderBottom ? props.borderBottom : ""};

  ${({disabled}) => disabled && css`
    cursor: not-allowed;
    color: gray;
  `}
`

export const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
`

export const UpdateHistoryBlock = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2.4rem;
`

export const UpdateHistoryItem = styled.section<UpdateHistoryProps>`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: .5rem;

  ${({disabled}) => disabled && css`
    text {
      color: var(--gray-300);
    }
  `}
`

export const Divider = styled.section<DividerProps>`
  width: 100%;
  border: .5px solid;
  border-color: ${props => props.bgColor ? props.bgColor : "var(--gray-100)"};
  margin: ${props => props.margin ? props.margin : ""};
`

export const Flex = styled.div`
  display: flex;
`

export const Button = styled.button<ButtonProps>`
  background-color: ${props => props.bgColor ? props.bgColor : "var(--blue-600)"};
  border-radius: ${props => props.borderRadius ? props.borderRadius : "2px"};
  padding: ${props => props.padding ? props.padding : "5px 16px"};
  color: ${props => props.color ? props.color : "white"};
  transition: ${props => props.transition ? props.transition : "all .2s ease-out"};
  border: ${props => props.border ? props.border : ""};
  width: ${props => props.width ? props.width : ""};
  height: ${props => props.height ? props.height : ""};
  display: ${props => props.display ? props.display : "flex"};
  align-items: ${props => props.align ? props.align : "center"};
  justify-content: ${props => props.justify ? props.justify : "center"};
  z-index: ${props => props.zIndex ? props.zIndex : ""};
  margin: ${props => props.margin ? props.margin : ""};
  position: ${props => props.position ? props.position : ""};
  top: ${props => props.top ? props.top : ""};
  right: ${props => props.right ? props.right : ""};
  left: ${props => props.left ? props.left : ""};
  bottom: ${props => props.bottom ? props.bottom : ""};

  &:hover {
    transition: ${props => props.transition ? props.transition : "all .2s ease-in"};
    filter: brightness(.95);
  }

  ${({ disabled }) => disabled && css`
    color: #00000040;
    cursor: not-allowed;

    &:hover {
      filter: none;
    }
  `}
`

export const TextArea = styled.textarea<TextAreaProps>`
  border: 1px solid;
  border-color: ${(props) => props.borderColor ? props.borderColor : "#d9d9d9"};
  border-radius: 2px;
`

export const Overlay = styled.div<OverlayProps>`
  position: fixed;
  height: 100vh;
  overflow-y: hidden;
  background-color: var(--white-025);
  z-index: 100;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;

  ${({isOpen}) => !isOpen && css`
    display: none;
  `}
`

export const Modal = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  z-index: 200;

  background-color: white;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.05), 0px 4px 20px rgba(0, 0, 0, 0.04);
`

interface ModalProps {
  hasContentUpdwards?: boolean;
  hasContentDownwards?: boolean;
}

export const ModalHeader = styled.div<ModalProps>`
  box-shadow: ${props => props.hasContentUpdwards ? "0px 12px 20px rgba(0, 0, 0, 0.03)" : ""};
  border-bottom: 1px solid var(--gray-100);
  height: 10%;
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
`

export const ModalBody = styled.div`
  overflow-y: scroll;
  padding: 1.5rem 2rem;

  height: 80%;
  width: 100%;
`

export const ModalFooter = styled.div<ModalProps>`
  box-shadow: ${props => props.hasContentDownwards ? "0px -12px 20px rgba(0, 0, 0, 0.03);" : ""};
  border-top: 1px solid var(--gray-100);
  height: 10%;
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
` 